import { json, jsonParseLinter } from '@codemirror/lang-json';
import { linter, lintGutter } from '@codemirror/lint';
import { okaidiaInit } from '@uiw/codemirror-theme-okaidia';
import CodeMirror, { type ViewUpdate } from '@uiw/react-codemirror';
import { useMemo } from 'react';

import { FormField } from '~/components/form/FormField';
import { Button } from '~/components/ui';
import { themeColor } from '~/lib/theme-colors';

interface JsonFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  /** The form's own check (e.g. "must be a JSON object"); the editor marks syntax errors inline. */
  error?: string;
  helperText?: string;
  required?: boolean;
  readOnly?: boolean;
  /** Editor height, a CSS length. */
  height?: string;
  testId?: string;
}

/** The pretty-printed value, or null when it is blank or not valid JSON. */
const formatted = (value: string): string | null => {
  if (!value.trim()) return null;
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return null;
  }
};

/** A JSON editor in the form-field frame: CodeMirror with JSON highlighting, inline syntax errors, and formatting on paste, on blur or by the Format action. */
export function JsonField({
  id,
  label,
  value,
  onChange,
  onBlur,
  error,
  helperText,
  required,
  readOnly = false,
  height = '160px',
  testId,
}: JsonFieldProps) {
  // A blank field is no data, not broken JSON, so the parse linter only runs on text.
  const extensions = useMemo(() => {
    const parseLinter = jsonParseLinter();
    return [
      json(),
      linter((view) => (view.state.doc.toString().trim() ? parseLinter(view) : [])),
      lintGutter(),
    ];
  }, []);
  // CodeMirror is a library surface, so its colours come from tokens (DESIGN.md §3.6).
  const theme = useMemo(
    () => okaidiaInit({ settings: { caret: themeColor('gray-300'), fontFamily: 'monospace' } }),
    [],
  );
  const pretty = formatted(value);

  // Pasted JSON is formatted at once; typed JSON when the field loses focus, never mid-keystroke.
  const handleChange = (next: string, update: ViewUpdate) => {
    const pasted = update.transactions.some((tr) => tr.isUserEvent('input.paste'));
    onChange((pasted && formatted(next)) || next);
  };
  const handleBlur = () => {
    if (!readOnly && pretty !== null && pretty !== value) onChange(pretty);
    onBlur?.();
  };

  return (
    <FormField
      id={id}
      label={label}
      required={required}
      helperText={helperText}
      error={error}
      testId={testId}
      labelAction={
        readOnly ? undefined : (
          <Button
            variant="link"
            disabled={pretty === null || pretty === value}
            onClick={() => pretty !== null && onChange(pretty)}
            testId={testId ? `${testId}-format` : undefined}
          >
            Format
          </Button>
        )
      }
    >
      <div
        className={`overflow-hidden rounded-lg border ${
          error ? 'border-error-500' : 'border-gray-300 dark:border-gray-700'
        }`}
      >
        <CodeMirror
          id={id}
          aria-label={label}
          value={value}
          height={height}
          theme={theme}
          extensions={extensions}
          editable={!readOnly}
          readOnly={readOnly}
          basicSetup={{ bracketMatching: true, foldGutter: true, lineNumbers: true }}
          onChange={handleChange}
          onBlur={handleBlur}
        />
      </div>
    </FormField>
  );
}
